import { useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { SalesTab } from './context';

type Patch = Record<string, string | null>;

const apply = (base: URLSearchParams, patch: Patch) => {
  const n = new URLSearchParams(base);
  for (const [k, v] of Object.entries(patch)) {
    if (v === null || v === '') n.delete(k);
    else n.set(k, v);
  }
  return n;
};

/**
 * The Sales page's URL state. react-router's setSearchParams does not queue functional
 * updates: two calls in one click both start from the same params, so the second drops the
 * first (a "Field Card →" link set slDoc but lost kitMode=lib and the Library never opened).
 * Every write here starts from the latest params, including a write made earlier in the
 * same click, so consecutive calls compose.
 */
export function useSalesParams() {
  const [params, setParams] = useSearchParams();
  const latest = useRef(params);
  latest.current = params;

  const patch = useCallback(
    (p: Patch) => {
      const n = apply(latest.current, p);
      latest.current = n;
      setParams(n, { replace: true });
    },
    [setParams],
  );
  const setParam = useCallback((k: string, v: string | null) => patch({ [k]: v }), [patch]);
  const setTab = useCallback((t: SalesTab, state?: Patch) => patch({ tab: t === 'CRM' ? null : t.toLowerCase(), ...(state ?? {}) }), [patch]);
  const openPrep = useCallback((facilityId: string | null) => patch({ mpOpen: facilityId ? null : '1', mpFor: facilityId }), [patch]);

  return { params, setParam, setTab, openPrep };
}
